import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useApiClient } from "../api/ApiClientContext";
import { ApiError } from "../api/types";
import { useAuth } from "../context/AuthContext";
import { PasswordField } from "../components/PasswordField";

const MIN_LENGTH_HINT = 12;

/**
 * Client-side checks below are hints only, shown so the user does not
 * submit an obviously unusable password. SFTPGo is the source of truth
 * for the real policy (min_entropy, docs/architecture/adr/0008, section
 * 2), and the BFF surfaces its rejection message if the hints pass but
 * the server still refuses the new password.
 */
function clientSideHints(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
): string[] {
  const hints: string[] = [];
  if (newPassword.length > 0 && newPassword.length < MIN_LENGTH_HINT) {
    hints.push(`Use at least ${MIN_LENGTH_HINT} characters.`);
  }
  if (newPassword.length > 0 && currentPassword.length > 0 && newPassword === currentPassword) {
    hints.push("Choose a new password different from your current one.");
  }
  if (confirmPassword.length > 0 && newPassword !== confirmPassword) {
    hints.push("The confirmation does not match the new password.");
  }
  return hints;
}

export function ChangePasswordPage() {
  const apiClient = useApiClient();
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const hints = clientSideHints(currentPassword, newPassword, confirmPassword);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(true);
    setServerError(null);

    if (currentPassword === "" || newPassword === "" || confirmPassword === "") {
      return;
    }
    if (hints.length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.changePassword({ currentPassword, newPassword });
      await refresh();
      navigate("/", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setServerError(caught.message);
      } else {
        setServerError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Paper sx={{ maxWidth: 440, width: "100%", p: { xs: 3, sm: 5 } }} elevation={0}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }} gutterBottom>
          Change your password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Enter your current password and choose a new one.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            {serverError && <Alert severity="error">{serverError}</Alert>}

            <PasswordField
              id="current-password"
              label="Current password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              error={touched && currentPassword === ""}
            />

            <PasswordField
              id="new-password"
              label="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              error={touched && newPassword === ""}
            />

            <PasswordField
              id="confirm-password"
              label="Confirm new password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={touched && confirmPassword === ""}
            />

            {touched && hints.length > 0 && (
              <Alert severity="warning">
                <Stack spacing={0.5}>
                  {hints.map((hint) => (
                    <span key={hint}>{hint}</span>
                  ))}
                </Stack>
              </Alert>
            )}

            <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}>
              {submitting ? "Updating..." : "Update password"}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
